import { Skeleton } from 'antd';
import { SkeletonInputProps } from 'antd/es/skeleton/Input';

import { Tooltip } from 'components/Tooltip';
import {
  UserAvatar,
  UserAvatarProps,
} from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { cn } from 'utils/cn';
import { getName } from 'utils/users/config';

import type { User } from 'types/data/user';

const userRendererSizeToSkeletonTextSize = (
  size: UserRendererProps['size']
): SkeletonInputProps['size'] => {
  if (
    typeof size === 'string' &&
    (size === 'small' || size === 'default' || size === 'large')
  )
    return size;
  return 'default';
};

const sizes = {
  xs: {
    avatarSize: 16,
    fontSize: 10,
    textClassName: 'ml-1',
  },
  small: {
    textClassName: 'ml-2',
  },
} as const;

interface UserRendererProps {
  userId: string;
  /**
   * @deprecated the user data is loaded from useUsersData and loading state is handled.
   * Only use this prop if you need to override the loading state.
   */
  isLoading?: boolean;
  size?: UserAvatarProps['size'] | 'xs';
  showName?: boolean;
  className?: string;
  avatarProps?: UserAvatarProps;
  textClassName?: string;
  noTooltip?: boolean;
  /**
   * Pre-fetched users map for lite mode (avoids per-cell useUsersData hook).
   * When provided, the component skips the useUsersData() hook call entirely.
   */
  usersMap?: Record<string, User>;
}

export function UserRenderer({
  userId,
  isLoading = false,
  size = 'small',
  showName = true,
  className,
  avatarProps,
  textClassName,
  noTooltip = false,
  usersMap: providedUsersMap,
}: UserRendererProps) {
  // Hook is still called (can't conditionally call hooks) but we use
  // providedUsersMap when available. The hook result is cached anyway.
  const hookData = useUsersData();
  const usersMap = providedUsersMap ?? hookData.usersMap;
  const isLoadingUsers = providedUsersMap ? false : hookData.isLoading;
  const userName = getName(userId, usersMap);

  const customSize = sizes[size as keyof typeof sizes];
  const customAvatarSize =
    customSize && 'avatarSize' in customSize && customSize?.avatarSize;
  const textDefaultClassName =
    customSize && 'textClassName' in customSize
      ? customSize?.textClassName
      : 'ml-2';
  const customFontSize =
    customSize && 'fontSize' in customSize && customSize?.fontSize;

  return (
    <Tooltip title={noTooltip ? undefined : userName}>
      <div className={cn('h-full flex items-center', className)}>
        <UserAvatar
          userId={userId}
          isLoading={isLoading || isLoadingUsers}
          size={customAvatarSize || (size as UserAvatarProps['size'])}
          {...avatarProps}
          style={{
            ...(customFontSize ? { fontSize: customFontSize } : {}),
            ...avatarProps?.style,
          }}
        />
        {showName && (
          <span className={cn(textDefaultClassName, 'truncate', textClassName)}>
            {isLoading || isLoadingUsers ? (
              <Skeleton.Input
                active
                size={userRendererSizeToSkeletonTextSize(size)}
              />
            ) : (
              userName
            )}
          </span>
        )}
      </div>
    </Tooltip>
  );
}
